/**
 * A fila de eventos: deduplica, agrupa em lotes e entrega ao transporte.
 *
 * Sem React e sem rede — o transporte é injetado — para ser testável. A
 * instância do app (com `fetch` e o token) está em `index.ts`.
 */
import { DEDUPE_MS, MAX_BATCH, toWire, type TrackInput, type WireEvent } from './events';

export interface TrackerOptions {
  /** Entrega um lote. Não espera nem relata falha: é telemetria. */
  send: (batch: WireEvent[]) => void;
  /** Sem sessão não se registra nada — atividade é de alguém. */
  isSignedIn: () => boolean;
  now?: () => number;
}

export interface Tracker {
  /** Enfileira; devolve `false` se o evento foi ignorado (sem sessão ou repetido). */
  track: (input: TrackInput, path: string) => boolean;
  /** Envia tudo o que está na fila, em lotes de até `MAX_BATCH`. */
  flush: () => void;
  pending: () => number;
}

export function createTracker({ send, isSignedIn, now = () => Date.now() }: TrackerOptions): Tracker {
  const queue: WireEvent[] = [];
  const recent = new Map<string, number>();

  const flush = () => {
    while (queue.length > 0) send(queue.splice(0, MAX_BATCH));
  };

  return {
    track(input, path) {
      if (!isSignedIn()) return false;
      const at = now();
      const key = `${input.action}|${input.targetId ?? ''}|${path}`;
      const last = recent.get(key);
      if (last !== undefined && at - last < DEDUPE_MS) return false;
      recent.set(key, at);
      // Só interessa o último segundo: o mapa não cresce com a navegação.
      for (const [k, t] of recent) if (at - t >= DEDUPE_MS) recent.delete(k);

      queue.push(toWire(input, path, at));
      // Lote cheio sai na hora: a fila nunca passa do tamanho de um lote.
      if (queue.length >= MAX_BATCH) flush();
      return true;
    },
    flush,
    pending: () => queue.length,
  };
}
